import { FC, useEffect } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { Colors } from "../../../configs";
import { CustomButton, CustomTextBox } from "../../atoms";
import { CustomDrawer } from "../../atoms/custom-drawer/custom-drawer";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import {
  getAllRoles,
  setUserRoleAction,
  signupAction,
  updateUserAction,
} from "../../../redux/actions/authAction";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";

const schema = Yup.object().shape({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  password: Yup.string().when("$isEdit", {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) =>
      schema.min(6, "Min 6 chars").required("Password is required"),
  }),
  role: Yup.mixed().required("Role is required"),
  storeId: Yup.mixed().when("$isEdit", {
    is: true,
    then: (schema) => schema.notRequired(),
    otherwise: (schema) => schema.required("Store is required"),
  }),
});

type FormData = Yup.InferType<typeof schema>;

export interface IAddUserDrawerProps {
  open: boolean;
  toggleDrawer: () => void;
  populateFor?: {
    id?: number;
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    role?: string;
    roleId?: number | string;
  };
}

export const AddUsersDrawer: FC<IAddUserDrawerProps> = ({
  open,
  toggleDrawer,
  populateFor,
}) => {
  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();
  const { signupLoading, signupData, updateLoading, roles } = useAppSelector(
    (state) => state.auth
  );
  const { adminDashboardSelectedMerchant } = useAppSelector(
    (state) => state.master
  );

  const isEdit = !!populateFor?.id;

  useEffect(() => {
    dispatch(getAllRoles());
  }, []);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(schema, { context: { isEdit } }),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      role: "",
      storeId: "",
    },
  });

  useEffect(() => {
    if (open) {
      reset({
        firstName: populateFor?.firstName ?? "",
        lastName: populateFor?.lastName ?? "",
        email: populateFor?.email ?? "",
        password: populateFor?.id ? "******" : "",
        role: populateFor?.role
          ? roles.find((r) => r.name === populateFor.role)?.id ?? ""
          : "",
        storeId: "",
      });
    }
  }, [open, populateFor, reset]);

  const handleSaveUser = async (data: FormData) => {
    try {
      if (populateFor?.id) {
        await dispatch(
          updateUserAction({
            id: populateFor.id,
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            avatar: "https://example.com/avatar.jpg",
          })
        ).unwrap();
        isEdit &&
          (await dispatch(
            setUserRoleAction({
              userId: populateFor.id,
              roleId:
                Number(populateFor.roleId) == Number(data.role)
                  ? Number(populateFor.roleId)
                  : Number(data.role),
            })
          ).unwrap());
        snackbar.showSnackbar("User updated successfully!", "success");
      } else {
        if (!adminDashboardSelectedMerchant?.whitelabel) {
          snackbar.showSnackbar(
            "Select a merchant/store before adding a user.",
            "error"
          );
          return;
        }
        await dispatch(
          signupAction({
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            password: data.password,
            userRoleId: Number(data.role),
            whiteLabelUrl: adminDashboardSelectedMerchant.whitelabel,
            storeId: Number(data.storeId),
          })
        ).unwrap();
        snackbar.showSnackbar("User added successfully!", "success");
      }
      toggleDrawer();
    } catch (err) {
      snackbar.showSnackbar("Failed to save user. Please try again.", "error");
    }
  };

  return (
    <CustomDrawer
      isOpen={open}
      toggleDrawer={toggleDrawer}
      sx={{ width: "28.65rem" }}
    >
      <form
        onSubmit={handleSubmit(handleSaveUser)}
        key={isEdit ? "edit" : "add"}
      >
        <Box
          sx={{
            paddingInline: "1.857rem",
            paddingTop: "2rem",
            display: "flex",
            flexDirection: "column",
            gap: "2.5rem",
          }}
        >
          <Typography
            sx={{
              fontWeight: "bold",
              fontSize: "1.4rem",
              lineHeight: "1.7rem",
              color: Colors.TEXT_GRAY_DARK,
            }}
          >
            {populateFor ? "Edit User" : "Add a New User"}
          </Typography>

          {/* Form fields */}
          {["firstName", "lastName", "email", "password"]
            .filter((fieldName) => {
              if (fieldName === "password" && populateFor?.password === "") {
                return false;
              }
              return true;
            })
            .map((fieldName) => {
              const label = fieldName
                .replace(/([A-Z])/g, " $1")
                .replace(/^./, (str) => str.toUpperCase());

              return (
                <Box
                  key={fieldName}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography sx={{ mb: 0.5 }}>{label}</Typography>
                  <Controller
                    name={fieldName as keyof FormData}
                    control={control}
                    render={({ field, fieldState }) => (
                      <CustomTextBox
                        {...field}
                        type={field.name === "password" ? "password" : "text"}
                        error={fieldState.invalid}
                        helperText={fieldState.error?.message}
                        sx={{ width: "100%", backgroundColor: Colors.WHITE }}
                        placeholder={`Enter ${label}`}
                      />
                    )}
                  />
                </Box>
              );
            })}
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}
          >
            <Typography>User Role</Typography>
            <Controller
              name="role"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl fullWidth>
                  <Select
                    {...field}
                    value={field.value}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    displayEmpty
                    sx={{ backgroundColor: Colors.WHITE }}
                    placeholder="Enter Role"
                  >
                    <MenuItem value="" disabled>
                      Select Role
                    </MenuItem>
                    {roles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.description}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldState.error && (
                    <Typography
                      sx={{ color: "red", mt: 1, fontSize: "0.8rem" }}
                    >
                      {fieldState.error.message}
                    </Typography>
                  )}
                </FormControl>
              )}
            />
          </Box>

          {!isEdit && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}
            >
              <Typography>Store</Typography>
              <Controller
                name="storeId"
                control={control}
                render={({ field, fieldState }) => (
                  <FormControl fullWidth>
                    <Select
                      {...field}
                      value={field.value}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                      displayEmpty
                      sx={{ backgroundColor: Colors.WHITE }}
                      placeholder="Select Store"
                    >
                      <MenuItem value="" disabled>
                        Select Store
                      </MenuItem>
                      {(adminDashboardSelectedMerchant?.stores ?? []).map(
                        (store) => (
                          <MenuItem key={store.storeId} value={store.storeId}>
                            {store.storeName}
                          </MenuItem>
                        )
                      )}
                    </Select>
                    {fieldState.error && (
                      <Typography
                        sx={{ color: "red", mt: 1, fontSize: "0.8rem" }}
                      >
                        {fieldState.error.message}
                      </Typography>
                    )}
                  </FormControl>
                )}
              />
            </Box>
          )}

          <CustomButton
            sx={{ width: "9.2rem", height: "2.6rem", marginTop: "1rem" }}
            type="submit"
            disabled={signupLoading || updateLoading}
          >
            {signupLoading || updateLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : populateFor ? (
              "Update User"
            ) : (
              "Add User"
            )}
          </CustomButton>
        </Box>
      </form>
    </CustomDrawer>
  );
};
